import { devices_settings } from 'src/stores/device-runtime/status';
import { format } from 'svelte-i18n';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { real_id } from 'src/helpers/interface_helpers';
import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
import {
	getStorageError,
	type NfsConfig,
	type StorageStatus,
	type StorageUsage,
} from '../types/camera-storage';
import { STORAGE_BUSY_ERRORS, STORAGE_STATE_LABELS } from './camera-errors';

const STORAGE_METHOD = {
	SET_CONFIG: 'Storage.SetConfig',
	FORMAT: 'Storage.Format',
	EJECT: 'Storage.Eject',
} as const;

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
const BYTES_PER_UNIT = 1024;
const PERCENT_MAX = 100;
const LOW_FREE_PERCENT = 20;
const CRITICAL_FREE_PERCENT = 10;

export type StorageSeverity = 'success' | 'warning' | 'danger';

export const isStorageBusy = (status: StorageStatus | undefined): boolean =>
	status?.errors?.some((error) => {
		const normalized = getStorageError(error);
		return normalized ? STORAGE_BUSY_ERRORS.includes(normalized) : false;
	}) ?? false;

export const getStorageComponentKey = (device?: Device | null): string => {
	return `storage:0`;
};

export const getStorageStatus = (device: Device): StorageStatus | undefined => {
	const id = real_id(device.id);
	return readDeviceStatus(id)?.[getStorageComponentKey(device)];
};

export const getStorageNfsConfig = (device: Device): NfsConfig | undefined => {
	const id = real_id(device.id);
	return readDeviceSettings(id)?.[getStorageComponentKey(device)]?.nfs;
};

export const formatBytes = (bytes: number): string => {
	if (!bytes || bytes <= 0) return `0 ${BYTE_UNITS[0]}`;

	let value = bytes;
	let unitIndex = 0;
	while (value >= BYTES_PER_UNIT && unitIndex < BYTE_UNITS.length - 1) {
		value /= BYTES_PER_UNIT;
		unitIndex += 1;
	}

	return `${value.toFixed(2)} ${BYTE_UNITS[unitIndex]}`;
};

export const getStorageUsage = (status: StorageStatus | undefined): StorageUsage => {
	const total = status?.fs_size || 0;
	const free = status?.fs_free || 0;
	const used = Math.max(total - free, 0);
	const freePercent = total > 0 ? Math.round((free / total) * PERCENT_MAX) : 0;
	const usedPercent = Math.min(PERCENT_MAX, Math.max(0, PERCENT_MAX - freePercent));

	return {
		used_label: formatBytes(used),
		total_label: formatBytes(total),
		free_percent: freePercent,
		used_percent: usedPercent,
	};
};

/**
 * Maps remaining free space to a severity used to color the storage usage indicator.
 * Low free space → warning (yellow), critically low → danger (red), otherwise success (green).
 */
export const getStorageSeverity = (freePercent: number): StorageSeverity => {
	if (freePercent <= CRITICAL_FREE_PERCENT) return 'danger';
	if (freePercent <= LOW_FREE_PERCENT) return 'warning';
	return 'success';
};

export const getStorageStateLabel = (
	status: StorageStatus | undefined,
): { key: string; default: string } | null => {
	const activeError = status?.errors
		?.map(getStorageError)
		.find((error) => error && STORAGE_STATE_LABELS[error]);
	if (activeError) return STORAGE_STATE_LABELS[activeError];

	if (!status?.present) {
		return { key: 'camera_storage_state_no_card', default: 'No SD card present' };
	}

	if (status.active) {
		return { key: 'camera_storage_state_ready', default: 'Ready' };
	}

	return null;
};

export const setStorageNfsConfig = async (
	device: Device,
	nfs: NfsConfig | null,
	successMessage: string,
): Promise<boolean> => {
	const _format = get(format);
	const channel = device?.channel || 0;
	const storageKey = getStorageComponentKey(device);

	const res: SettingRequest = await gen2Request(device, STORAGE_METHOD.SET_CONFIG, {
		id: channel,
		config: { nfs },
	});
	onSettingsResponse(res, device, {
		success: successMessage,
		danger: _format('camera_storage_save_error', {
			default: 'Could not apply storage settings. Please retry.',
		}),
	});

	if (!res?.isok) return false;

	const id = real_id(device.id);
	const settings = readDeviceSettings(id);
	devices_settings.update_devices_settings(id, {
		...settings,
		[storageKey]: { ...settings?.[storageKey], nfs },
	});
	return true;
};

export const formatStorage = async (device: Device): Promise<boolean> => {
	const _format = get(format);
	const channel = device?.channel || 0;

	const res: SettingRequest = await gen2Request(device, STORAGE_METHOD.FORMAT, { id: channel });
	onSettingsResponse(res, device, {
		success: _format('camera_storage_format_started_toast', {
			default: 'Formatting started.',
		}),
		danger: _format('camera_storage_format_error', {
			default: 'Could not format the storage. Please retry.',
		}),
	});
	return !!res?.isok;
};

export const ejectStorage = async (device: Device): Promise<boolean> => {
	const _format = get(format);
	const channel = device?.channel || 0;

	const res: SettingRequest = await gen2Request(device, STORAGE_METHOD.EJECT, { id: channel });
	onSettingsResponse(res, device, {
		success: _format('camera_storage_eject_success_toast', {
			default: 'Storage ejected.',
		}),
		danger: _format('camera_storage_eject_error', {
			default: 'Could not eject the storage. Please retry.',
		}),
	});
	return !!res?.isok;
};
